import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, RefreshCw, PenTool, X } from 'lucide-react';
import { Button } from './ui/button';
import { GoldenQuestionIcon } from './GoldenQuestionIcon';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GoldenQuestion({ bookId, chapterId, onWriteAnswer, compact = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [questionsRemaining, setQuestionsRemaining] = useState(null);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/golden-question/usage`,
        { withCredentials: true }
      );
      setUsage(response.data);
      setQuestionsRemaining(response.data.questions_remaining);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const requestQuestion = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/golden-question`,
        { book_id: bookId, chapter_id: chapterId },
        { withCredentials: true }
      );
      
      setQuestion(response.data.question);
      setSuggestion(response.data.suggestion);
      setQuestionsRemaining(response.data.questions_remaining);
      
      // Update usage
      if (usage) {
        setUsage({
          ...usage,
          questions_used: usage.questions_used + 1,
          questions_remaining: response.data.questions_remaining
        });
      }
    } catch (error) {
      console.error('Error getting golden question:', error);
      toast.error('Erro ao gerar pergunta');
    } finally {
      setLoading(false);
    }
  };

  const handleWriteAnswer = () => {
    setIsOpen(false);
    if (onWriteAnswer) {
      onWriteAnswer(question);
    }
  };

  const handleAnotherQuestion = () => {
    if (questionsRemaining === 0) {
      toast.error('Você atingiu o limite semanal de Perguntas de Ouro');
      return;
    }
    requestQuestion();
  };

  // Compact trigger button (icon only with tooltip)
  if (!isOpen && compact) {
    return (
      <div className="relative group">
        <button
          onClick={() => {
            setIsOpen(true);
            if (!question) {
              requestQuestion();
            }
          }}
          className="hover:scale-110 transition-transform relative"
          data-testid="golden-question-trigger"
        >
          <GoldenQuestionIcon size={32} />
          {usage && usage.questions_remaining > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm">
              {usage.questions_remaining}
            </span>
          )}
        </button>
        {/* Tooltip on hover - positioned to the left */}
        <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
          <div className="bg-stone-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
            <span className="font-medium text-amber-400">Pergunta de Ouro</span>
            <p className="text-stone-300 mt-0.5">Inspiração para destravar sua escrita</p>
          </div>
          {/* Arrow pointing right */}
          <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-2 h-2 bg-stone-800 rotate-45"></div>
        </div>
      </div>
    );
  }

  // Full trigger button
  if (!isOpen) {
    return (
      <Button
        onClick={() => {
          setIsOpen(true);
          if (!question) {
            requestQuestion();
          }
        }}
        className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-stone-900 shadow-lg shadow-amber-500/20"
        data-testid="golden-question-trigger"
      >
        <GoldenQuestionIcon size={20} className="mr-2" />
        Pergunta de Ouro
        {usage && usage.questions_remaining > 0 && (
          <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
            {usage.questions_remaining}
          </span>
        )}
      </Button>
    );
  }

  // Modal
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div 
        className="bg-card border border-amber-500/30 rounded-2xl max-w-lg w-full shadow-2xl shadow-amber-500/10 overflow-hidden"
        data-testid="golden-question-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500/10 to-amber-600/10 p-6 border-b border-amber-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <GoldenQuestionIcon size={48} />
              <div>
                <h2 className="text-xl font-serif">Pergunta de Ouro</h2>
                <p className="text-sm text-muted-foreground">
                  {questionsRemaining !== null && questionsRemaining > 0 
                    ? `${questionsRemaining} pergunta${questionsRemaining !== 1 ? 's' : ''} restante${questionsRemaining !== 1 ? 's' : ''}`
                    : questionsRemaining === 0 
                      ? 'Limite semanal atingido'
                      : ''
                  }
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-muted-foreground hover:text-foreground p-2 hover:bg-muted rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 relative">
                <div className="absolute inset-0 rounded-full border-4 border-amber-500/20"></div>
                <div className="absolute inset-0 rounded-full border-4 border-amber-500 border-t-transparent animate-spin"></div>
                <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-amber-500 animate-pulse" />
              </div>
              <p className="text-muted-foreground">Gerando sua Pergunta de Ouro...</p>
            </div>
          ) : question ? (
            <div className="space-y-6">
              {/* Question */}
              <div className="text-center">
                <p className="text-2xl font-serif leading-relaxed text-foreground">
                  "{question}"
                </p>
              </div>

              {/* Suggestion */}
              {suggestion && (
                <p className="text-center text-muted-foreground italic">
                  {suggestion}
                </p>
              )}

              {/* Limit reached message */}
              {questionsRemaining === 0 && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-center">
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    Você já usou suas 3 Perguntas de Ouro desta semana.
                    <br />
                    <span className="font-medium">No Premium, suas perguntas continuam abertas.</span>
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <Button
                  onClick={handleWriteAnswer}
                  className="bg-amber-500 hover:bg-amber-600 text-stone-900 w-full"
                  data-testid="write-answer-button"
                >
                  <PenTool className="mr-2" size={18} />
                  Responder no Capítulo
                </Button>
                
                {questionsRemaining > 0 && (
                  <Button
                    variant="outline"
                    onClick={handleAnotherQuestion}
                    disabled={loading}
                    className="w-full"
                    data-testid="another-question-button"
                  >
                    <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
                    Quero Outra Pergunta
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
